

import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { alpha, useTheme } from "@mui/material/styles";
import InputAdornment from "@mui/material/InputAdornment";
import { bgGradient } from "../../../theme/css";
import Iconify from "../../../Components/iconify/iconify";
import { useAuth } from "../../../context";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAdd from "../../../api/useAdd";
import { BACKGROUND_URL } from "../../../config";

// ----------------------------------------------------------------------

const SignInView = () => {
  const { t } = useTranslation();
  const authLogin = useAuth();
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = useState("");

  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const { handleAdd, loading, resetForm, responseData } = useAdd(
    "login",
    null,
    "Welcome Again!",
  );

  useEffect(() => {
    if (resetForm) {
      authLogin.login(responseData);
      navigate("/");
    }
  }, [resetForm, authLogin, navigate, responseData]);

  const handleSignIn = (e) => {
    e.preventDefault();
    let isValid = true;

    if (!username) {
      setUsernameError(t("sign_in.userNameRequired"));
      isValid = false;
    } else {
      setUsernameError("");
    }

    if (!password) {
      setPasswordError(t("sign_in.passwordRequired"));
      isValid = false;
    } else {
      setPasswordError("");
    }

    if (isValid) {
      const formData = new FormData();
      formData.append("username", username);
      formData.append("password", password);
      handleAdd(formData);
    }
  };

  return (
    <Box
      sx={{
        ...bgGradient({
          color: alpha(theme.palette.background.default, 0.15),
          imgUrl: BACKGROUND_URL,
        }),
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: theme.spacing(2),
      }}
    >
      <Card
        elevation={8}
        sx={{
          p: { xs: 3, sm: 5 },
          width: "100%",
          maxWidth: 550,
          borderRadius: 3,
          boxShadow: theme.shadows[10],
          backgroundColor: alpha(theme.palette.background.paper, 0.9),
        }}
      >
        <Stack spacing={2} alignItems="center">
          <Typography variant="h4" component="h1" gutterBottom>
            {t("sign_in.signIn")}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            {t("sign_in.dontHaveAccount")}
            <Link href="#" variant="subtitle2" sx={{ ml: 0.5 }}>
              {t("sign_in.getStarted")}
            </Link>
          </Typography>
        </Stack>

        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button
            fullWidth
            size="large"
            color="primary"
            variant="contained"
            startIcon={<Iconify icon="eva:google-fill" />}
            sx={{
              backgroundColor: "#DF3E30",
              "&:hover": { backgroundColor: "#c1351d" },
              transition: "background-color 0.3s",
            }}
          >
            Google
          </Button>

          <Button
            fullWidth
            size="large"
            color="primary"
            variant="contained"
            startIcon={<Iconify icon="eva:facebook-fill" />}
            sx={{
              backgroundColor: "#1877F2",
              "&:hover": { backgroundColor: "#1558b0" },
              transition: "background-color 0.3s",
            }}
          >
            Facebook
          </Button>
        </Stack>

        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Button
            fullWidth
            size="large"
            color="primary"
            variant="contained"
            startIcon={<Iconify icon="eva:twitter-fill" />}
            sx={{
              backgroundColor: "#1C9CEA",
              "&:hover": { backgroundColor: "#1877F2" },
              transition: "background-color 0.3s",
            }}
          >
            Twitter
          </Button>
        </Stack>

        <Divider sx={{ my: 4 }}>
          <Typography variant="body2" color="text.secondary">
            {t("sign_in.or")}
          </Typography>
        </Divider>

        <Box component="form" onSubmit={handleSignIn}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label={t("sign_in.username")}
              variant="outlined"
              name="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              error={Boolean(usernameError)}
              helperText={usernameError}
              autoComplete="username"
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              label={t("sign_in.password")}
              variant="outlined"
              name="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={Boolean(passwordError)}
              helperText={passwordError}
              autoComplete="current-password"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      aria-label="toggle password visibility"
                    >
                      <Iconify
                        icon={
                          showPassword ? "eva:eye-fill" : "eva:eye-off-fill"
                        }
                        sx={{ color: "text.secondary" }}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ my: 3 }}
          >
            <Link href="#" variant="body2" underline="hover">
              {t("sign_in.forgotPassword")}
            </Link>
          </Stack>

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            color="primary"
            loading={loading}
            sx={{
              py: 1.5,
              textTransform: "none",
              fontSize: "1rem",
              borderRadius: 2,
            }}
          >
            {t("sign_in.login")}
          </LoadingButton>
        </Box>
      </Card>
    </Box>
  );
};

export default SignInView;
